// CKYCForm.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
const { axiosPOSTCall1 } = require('../../utilities/utils/apiPost');

// Center the form fully with a cleaner white background
const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 2rem;
  background-color: #ffffff;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1000px;
  padding: 2rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
  font-family: 'Segoe UI', sans-serif;
`;

const Header = styled.h2`
  margin-bottom: 2rem;
  font-weight: 600;
  text-align: center;
`;

const FormRow = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const Field = styled.div`
  flex: 1;
  min-width: 250px;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 1rem;
  border-radius: 6px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Select = styled.select`
  padding: 0.6rem;
  border-radius: 5px;
  border: 1px solid #ccc;
`;

const Input = styled.input`
  padding: 0.6rem;
  border-radius: 5px;
  border: 1px solid #ccc;
`;

const ReadOnlyInput = styled(Input)`
  background-color: #f1f3f5;
  color: #333;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const BackButton = styled.button`
  background-color:rgb(197, 64, 64);
  color: #000;
  border: 1px solid #ccc;
  padding: 0.6rem 1.2rem;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: #e74c3c;
  }
`;

const ProceedButton = styled.button`
  background-color: #f58220;
  color: white;
  border: none;
  padding: 0.6rem 1.5rem;
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: #e3720f;
  }
`;

const fgKycRedirection = (url, proposalId, returnUrl) => {
  // Create container div to show the "redirecting" message
  const messageDiv = document.createElement('div');
  messageDiv.style.textAlign = 'center';
  messageDiv.style.marginTop = '50px';
  messageDiv.innerHTML = `
    <strong>You are being redirected to KYC portal</strong><br/>
    <span style="color:blue;">Please wait ...</span><br/>
    <small>(Please do not press 'Refresh' or 'Back' button)</small>
  `;
  document.body.appendChild(messageDiv);

  // Create the form
  const form = document.createElement('form');
  form.id = 'KYCFailedFormTag';
  form.method = 'post';
  form.name = 'redirect';
  form.action = url;

  // Hidden inputs
  const inputs = {
    VISoF_KYC_Req_No: proposalId,
    IC_KYC_No: proposalId,
    VISoF_Return_URL: returnUrl,
  };

  Object.entries(inputs).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.id = key;
    input.value = value;
    form.appendChild(input);
  });

  // Append form to body
  document.body.appendChild(form);

  // Submit form after a tiny delay (to ensure DOM updates)
  setTimeout(() => {
    form.submit();
  }, 100);  // 100ms delay to show message before redirect
};



const CKYCForm = () => {
  const navigate = useNavigate();
  const [documentType, setDocumentType] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [enquiryId, setEnquiryId] = useState('');
  const [dob, setDob] = useState(''); // add DOB state

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const id = queryParams.get('enquiryId');
    setEnquiryId(id);

    // Optionally set DOB default value here if needed
    // setDob('06-05-1975'); // or fetch from somewhere
  }, []);

  const handleSubmit = async () => {
    if (!documentType || !documentNumber || !dob) {
      alert('Please fill all required fields');
      return;
    }

    try {
      const payload = {
        enquiryId,
        panNo: documentType === 'PAN' ? documentNumber : '',
        dob: moment(dob).format('DD-MM-YYYY'),
      };

      // const response = await axios.post(
      //   'http://localhost:3001/api/v1/healthInsurance/health/verifyKyc/health-fg',
      //   payload
      // );
      await axiosPOSTCall1(
        '/healthInsurance/health/verifyKyc/health-fg',
        payload,
        (callback) => {
          const data = callback.data;
          const verified = data?.verified;
          const kycURL = data?.kycUrl;
          const proposalId = data?.proposalId;
          const returnUrl = data?.redirectURL;

          if (kycURL && kycURL.trim() !== '' && verified === false) {
            fgKycRedirection(
              kycURL,
              proposalId,
              returnUrl
            );
            
            // window.location.href = kycURL;
          } else if (verified === true) {
            // navigate to next page
            navigate(`/proposal-form?enquiryId=${enquiryId}`);
          } else {
            alert('Unexpected response from KYC verification');
          }
        }
      );
    } catch (error) {
      console.error('API error:', error);
      alert('Failed to verify KYC. Please try again.');
    }
  };

  return (
    <Wrapper>
      <Container>
        <Header>CKYC Detail</Header>
        <FormRow>
          <Field>
            <Label>Document Type *</Label>
            <Select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
            >
              <option value="">Select Document Type</option>
              <option value="PAN">PAN</option>
              <option value="Aadhaar">Aadhaar</option>
            </Select>
          </Field>

          <Field>
            <Label>Document No. *</Label>
            <Input
              type="text"
              placeholder="DOCUMENT NUMBER"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
            />
          </Field>

          <Field>
            <Label>Date of Birth *</Label>
            <Input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}

            />
          </Field>
        </FormRow>

        <ButtonGroup>
          <BackButton onClick={() => navigate(-1)}>&larr; Back to Quotation</BackButton>
          <ProceedButton onClick={handleSubmit}>Proceed</ProceedButton>
        </ButtonGroup>
      </Container>
    </Wrapper>
  );
};

export default CKYCForm;
